package com.arenaproplay.admin.data.http

import com.arenaproplay.admin.core.config.AppConfig
import com.arenaproplay.admin.core.logger.getLogger
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.engine.js.*
import io.ktor.client.plugins.*
import io.ktor.client.plugins.auth.*
import io.ktor.client.plugins.auth.providers.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.plugins.logging.*
import io.ktor.client.request.*
import io.ktor.http.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

object HttpClientFactory {
    private val logger = getLogger<HttpClientFactory>()

    /** Canonical platform tag the backend recognises in `X-Client-Platform`. */
    const val CLIENT_PLATFORM = "admin-web"

    fun createDefault(): HttpClient {
        return HttpClient(Js) {
            install(ContentNegotiation) {
                json(
                    Json {
                        ignoreUnknownKeys = true
                        isLenient = true
                        encodeDefaults = true
                        prettyPrint = AppConfig.environment == com.arenaproplay.admin.core.config.Environment.DEVELOPMENT
                    },
                )
            }

            if (AppConfig.environment == com.arenaproplay.admin.core.config.Environment.DEVELOPMENT) {
                install(Logging) {
                    level = LogLevel.ALL
                    logger =
                        object : io.ktor.client.plugins.logging.Logger {
                            override fun log(message: String) {
                                this@HttpClientFactory.logger.debug(message)
                            }
                        }
                }
            }

            defaultRequest {
                header("X-Client-Platform", CLIENT_PLATFORM)
                header("X-Client-Version", AppConfig.appVersion)
                // Defeat browser HTTP caching of API GETs. Bunny injects
                // `Cache-Control: public, max-age=0` (origin sets none), so the
                // browser may serve a stale cached JSON to post-mutation refetches
                // — the list then only updates on a full page reload. These request
                // directives force revalidation → always fresh. (CORS allow-lists
                // both headers on the backend.)
                header("Cache-Control", "no-cache")
                header("Pragma", "no-cache")
            }

            expectSuccess = false
        }
    }

    /**
     * Authenticated HTTP client with built-in JWT refresh.
     *
     * The Ktor `Auth { bearer { ... } }` provider attaches Bearer headers
     * automatically and, on 401, calls `POST /v1/auth/refresh` with the
     * stored refresh token, persists the new pair via [onTokensRefreshed],
     * and replays the original request. If the refresh itself fails (e.g.
     * refresh token expired), [onAuthFailed] fires so the caller can wipe
     * the session and bounce the user to /login.
     */
    fun createAuthenticated(
        accessToken: String,
        refreshToken: String,
        onTokensRefreshed: (accessToken: String, refreshToken: String) -> Unit,
        onAuthFailed: () -> Unit,
    ): HttpClient {
        return HttpClient(Js) {
            install(ContentNegotiation) {
                json(
                    Json {
                        ignoreUnknownKeys = true
                        isLenient = true
                        encodeDefaults = true
                    },
                )
            }

            if (AppConfig.environment == com.arenaproplay.admin.core.config.Environment.DEVELOPMENT) {
                install(Logging) {
                    level = LogLevel.ALL
                    logger =
                        object : io.ktor.client.plugins.logging.Logger {
                            override fun log(message: String) {
                                this@HttpClientFactory.logger.debug(message)
                            }
                        }
                }
            }

            install(Auth) {
                bearer {
                    loadTokens { BearerTokens(accessToken, refreshToken) }

                    refreshTokens {
                        val current = oldTokens?.refreshToken ?: refreshToken
                        try {
                            val response =
                                client.post(AppConfig.getApiUrl("/v1/auth/refresh")) {
                                    contentType(ContentType.Application.Json)
                                    setBody(RefreshRequestBody(current))
                                    markAsRefreshTokenRequest()
                                }
                            if (!response.status.isSuccess()) {
                                logger.warn("Refresh failed: ${response.status}")
                                onAuthFailed()
                                return@refreshTokens null
                            }
                            val envelope = response.body<RefreshEnvelope>()
                            val payload =
                                envelope.data ?: run {
                                    onAuthFailed()
                                    return@refreshTokens null
                                }
                            onTokensRefreshed(payload.accessToken, payload.refreshToken)
                            BearerTokens(payload.accessToken, payload.refreshToken)
                        } catch (
                            @Suppress("TooGenericExceptionCaught") e: Throwable,
                        ) {
                            logger.warn("Refresh error: ${e.message}")
                            onAuthFailed()
                            null
                        }
                    }

                    // Eagerly attach Bearer on every request so we don't pay
                    // the unauthorised-then-retry round-trip on first hit.
                    sendWithoutRequest { true }
                }
            }

            defaultRequest {
                header("Content-Type", "application/json")
                header("Accept", "application/json")
                header("X-Client-Platform", CLIENT_PLATFORM)
                header("X-Client-Version", AppConfig.appVersion)
                // Force revalidation so post-mutation refetches never read a
                // stale browser-cached response (see createDefault for details).
                header("Cache-Control", "no-cache")
                header("Pragma", "no-cache")
            }

            expectSuccess = false
        }
    }
}

@Serializable
private data class RefreshRequestBody(
    @SerialName("refreshToken") val refreshToken: String,
)

@Serializable
private data class RefreshEnvelope(
    @SerialName("data") val data: RefreshPayload? = null,
)

@Serializable
private data class RefreshPayload(
    @SerialName("accessToken") val accessToken: String,
    @SerialName("refreshToken") val refreshToken: String,
)

fun HttpRequestBuilder.addDefaultHeaders() {
    header("Content-Type", "application/json")
    header("Accept", "application/json")
}

fun HttpRequestBuilder.addAuthHeader(token: String) {
    header("Authorization", "Bearer $token")
}

fun HttpRequestBuilder.setBaseUrl(baseUrl: String) {
    val currentUrl = url.buildString()
    if (!currentUrl.startsWith(baseUrl)) {
        url(baseUrl)
    }
}
// trigger recompile 1781041463
